/**
 * Pending Agent — Books assigned to agents, awaiting their completion.
 *
 * Groups books by assigned agent. Shows both:
 * - book_scan_requests with status 'assigned' or 'scanning' or 'editing'
 * - bookstore_books with assigned_agent_id set (not yet in scanned_books pipeline)
 *
 * Books disappear from here when agent clicks "Finish & Submit for Review"
 * (quality_status → turn_codes_pending).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, UserCheck, ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';
import { toast } from 'sonner';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bookstoreTable = () => (supabase as any).from('bookstore_books');

interface PendingBook {
  id: string;
  title: string;
  author: string | null;
  isbn: string | null;
  status: string;
  assigned_agent_id: string;
  source: 'parent_request' | 'bookstore';
  assigned_at: string | null;
  requested_by_name: string | null;
  library_request_status: string;
}

interface Agent {
  id: string;
  name: string;
  email: string | null;
}

export function PendingAgentPage() {
  const [books, setBooks] = useState<PendingBook[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [requestsRes, bookstoreRes, agentsRes] = await Promise.all([
        // Parent requests assigned to agents (not yet published)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('book_scan_requests')
          .select('id, title, author, isbn, status, assigned_to, assigned_at, requested_by, library_request_status')
          .in('status', ['assigned', 'scanning', 'editing'])
          .not('assigned_to', 'is', null)
          .is('deleted_at', null)
          .order('assigned_at', { ascending: false }),
        // Bookstore books assigned to agents
        bookstoreTable()
          .select('id, title, author, isbn, assigned_agent_id, library_request_status')
          .not('assigned_agent_id', 'is', null)
          .order('title'),
        // All scanning agents
        supabase
          .from('profiles')
          .select('id, name, email')
          .eq('admin_level', 'scanning_agent'),
      ]);

      // Resolve requester names for parent requests
      const requesterIds = new Set<string>();
      for (const r of requestsRes.data || []) {
        if (r.requested_by) requesterIds.add(r.requested_by as string);
      }
      let requesterMap = new Map<string, string>();
      if (requesterIds.size > 0) {
        const { data: requesterProfiles } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', [...requesterIds]);
        requesterMap = new Map((requesterProfiles || []).map((p: { id: string; name: string }) => [p.id, p.name]));
      }

      const requestBooks: PendingBook[] = (requestsRes.data || []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        title: r.title as string || 'Untitled',
        author: r.author as string | null,
        isbn: r.isbn as string | null,
        status: r.status as string,
        assigned_agent_id: r.assigned_to as string,
        source: 'parent_request' as const,
        assigned_at: r.assigned_at as string | null,
        requested_by_name: r.requested_by ? (requesterMap.get(r.requested_by as string) || null) : null,
        library_request_status: (r.library_request_status as string) || 'not_requested',
      }));

      const storeBooks: PendingBook[] = (bookstoreRes.data || []).map((b: Record<string, unknown>) => ({
        id: b.id as string,
        title: b.title as string || 'Untitled',
        author: b.author as string | null,
        isbn: b.isbn as string | null,
        status: 'assigned',
        assigned_agent_id: b.assigned_agent_id as string,
        source: 'bookstore' as const,
        assigned_at: null,
        requested_by_name: null,
        library_request_status: (b.library_request_status as string) || 'not_requested',
      }));

      setBooks([...requestBooks, ...storeBooks]);
      setAgents(agentsRes.data || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleReassign = async (book: PendingBook, newAgentId: string) => {
    if (newAgentId === book.assigned_agent_id) return;
    setActionInProgress(book.id);
    try {
      if (book.source === 'parent_request') {
        const { error } = await supabase
          .from('book_scan_requests')
          .update({ assigned_to: newAgentId, assigned_at: new Date().toISOString(), updated_at: new Date().toISOString() })
          .eq('id', book.id);
        if (error) { toast.error(error.message); return; }
      } else {
        const { error } = await bookstoreTable()
          .update({ assigned_agent_id: newAgentId, updated_at: new Date().toISOString() })
          .eq('id', book.id);
        if (error) { toast.error(error.message); return; }
      }

      setBooks(prev => prev.map(b => b.id === book.id ? { ...b, assigned_agent_id: newAgentId } : b));
      const agent = agents.find(a => a.id === newAgentId);
      toast.success(`Reassigned to ${agent?.name || 'agent'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleLibraryStatusChange = async (book: PendingBook, newStatus: string) => {
    setActionInProgress(book.id);
    try {
      if (book.source === 'parent_request') {
        const { error } = await supabase
          .from('book_scan_requests')
          .update({ library_request_status: newStatus, updated_at: new Date().toISOString() })
          .eq('id', book.id);
        if (error) { toast.error(error.message); return; }
      } else {
        const { error } = await bookstoreTable()
          .update({ library_request_status: newStatus, updated_at: new Date().toISOString() })
          .eq('id', book.id);
        if (error) { toast.error(error.message); return; }
      }
      setBooks(prev => prev.map(b => b.id === book.id ? { ...b, library_request_status: newStatus } : b));
    } finally {
      setActionInProgress(null);
    }
  };

  // Group books by agent
  const booksByAgent = useMemo(() => {
    const map = new Map<string, PendingBook[]>();
    for (const book of books) {
      const list = map.get(book.assigned_agent_id) || [];
      list.push(book);
      map.set(book.assigned_agent_id, list);
    }
    return map;
  }, [books]);

  const statusLabel = (status: string) => {
    switch (status) {
      case 'assigned': return { text: 'Assigned', color: 'bg-[#0A84FF]/15 text-[#0A84FF]' };
      case 'scanning': return { text: 'Scanning', color: 'bg-[#FF9F0A]/15 text-[#FF9F0A]' };
      case 'editing': return { text: 'Editing', color: 'bg-[#BF5AF2]/15 text-[#BF5AF2]' };
      default: return { text: status, color: 'bg-black/[0.04] text-[#8E8E93]' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#1C1C1E]">Pending Agent Work</h2>
        <p className="text-sm text-[#8E8E93] mt-0.5">
          {books.length} book{books.length !== 1 ? 's' : ''} assigned across {booksByAgent.size} agent{booksByAgent.size !== 1 ? 's' : ''}
        </p>
      </div>

      {books.length === 0 && (
        <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
          <p className="text-[#8E8E93] text-sm">No books currently assigned to agents.</p>
        </div>
      )}

      {agents.map(agent => {
        const agentBooks = booksByAgent.get(agent.id);
        if (!agentBooks?.length) return null;

        return (
          <div key={agent.id} className="mb-6">
            {/* Agent header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[#111111]/10 flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-[#1A1A1A]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1C1C1E]">{agent.name}</h3>
                <p className="text-xs text-[#8E8E93]">{agentBooks.length} book{agentBooks.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Books list */}
            <div className="space-y-2 ml-11">
              {agentBooks.map(book => {
                const sl = statusLabel(book.status);
                const isActioning = actionInProgress === book.id;

                return (
                  <div key={book.id} className="flex items-center gap-3 rounded-xl p-3" style={glassStyles.card}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1C1C1E] truncate">{book.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {book.author && <span className="text-xs text-[#8E8E93]">{book.author}</span>}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          book.source === 'parent_request' ? 'bg-[#111111]/10 text-[#1A1A1A]' : 'bg-black/[0.04] text-[#8E8E93]'
                        }`}>
                          {book.source === 'parent_request'
                            ? (book.requested_by_name ? `Requested by ${book.requested_by_name}` : 'Request')
                            : 'Bookstore'}
                        </span>
                      </div>
                    </div>

                    {/* Status */}
                    <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${sl.color}`}>
                      {sl.text}
                    </span>

                    {/* Library request status */}
                    <select
                      value={book.library_request_status}
                      onChange={e => handleLibraryStatusChange(book, e.target.value)}
                      disabled={isActioning}
                      className={`shrink-0 px-2 py-1.5 rounded-lg text-xs border focus:outline-none focus:border-[#1A1A1A] disabled:opacity-30 ${
                        book.library_request_status === 'requested_from_library'
                          ? 'border-[#34C759] text-[#34C759] bg-[#34C759]/5'
                          : 'border-[#D4D4D4] text-[#888888] bg-white'
                      }`}
                    >
                      <option value="not_requested">Not requested</option>
                      <option value="requested_from_library">Requested from library</option>
                    </select>

                    {/* Reassign */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <ArrowRight className="w-3 h-3 text-[#8E8E93]" />
                      <select
                        value={book.assigned_agent_id}
                        onChange={e => handleReassign(book, e.target.value)}
                        disabled={isActioning}
                        className="px-2 py-1.5 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-xs focus:outline-none focus:border-[#1A1A1A] disabled:opacity-30"
                      >
                        {agents.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PendingAgentPage;
